import React, { FC, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper";
import { Button } from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { ScreenSize } from "@constants/screen-size";
import "swiper/css";
import "swiper/css/pagination";

export interface IntroSlideConfig {
  asset: string;
  isLast?: boolean;
}

const INTRO_SLIDE_CONFIGS: IntroSlideConfig[] = [
  { asset: "intro-1.png" },
  { asset: "intro-2.png" },
  { asset: "intro-3.png" },
  { asset: "intro-4.png", isLast: true },
];

const CarouselContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: 24px;
  box-sizing: border-box;

  @media (max-width: ${ScreenSize.sm}px) {
    padding: 16px;
  }
`;

const SwiperWrapper = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  margin: 0;

  .intro-swiper {
    height: 100%;
    margin: 0;
  }

  .swiper-pagination {
    position: relative;
    margin-top: 24px;
    margin-bottom: 8px;
  }

  .swiper-pagination-bullet {
    width: 8px;
    height: 8px;
    background: #c4c4c4;
    opacity: 1;
    transition: background 0.2s;
  }

  .swiper-pagination-bullet-active {
    background: #1e64dd;
    width: 24px;
    border-radius: 4px;
  }
`;

const SlideContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  padding: 16px 0;
  text-align: center;
`;

const SlideImage = styled.img`
  width: 100%;
  max-width: 280px;
  height: auto;
  object-fit: contain;
  flex-shrink: 0;
`;

const SlideTitle = styled.h2`
  font-size: 30px;
  line-height: 1.3;
  font-weight: 500;
  color: #171a1f;
  margin: 0 0 12px;

  @media (min-width: ${ScreenSize.sm}px) {
    font-size: 28px;
  }
`;

const SlideDescription = styled.p`
  font-size: 16px;
  line-height: 1.5;
  font-weight: 400;
  color: #565d6d;
  margin: 0;

  @media (min-width: ${ScreenSize.sm}px) {
    font-size: 17px;
  }
`;

const SlideImageWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: 24px;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  padding: 24px 0 0;
  flex-shrink: 0;
`;

export interface IntroCarouselProps {
  onComplete?: () => void;
}

const IntroCarousel: FC<IntroCarouselProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleContinue = () => {
    const config = INTRO_SLIDE_CONFIGS[activeIndex];
    if (config?.isLast && onComplete) {
      onComplete();
      return;
    }
    swiperInstance?.slideNext();
  };

  return (
    <CarouselContainer>
      <SwiperWrapper>
        <Swiper
          className="intro-swiper"
          onSwiper={setSwiperInstance}
          onSlideChange={(swiper: SwiperClass) => setActiveIndex(swiper.activeIndex)}
          modules={[Pagination]}
          pagination={{ clickable: true }}
          allowTouchMove
          simulateTouch
          resistanceRatio={0}
          spaceBetween={0}
        >
          {INTRO_SLIDE_CONFIGS.map((config, index) => {
            const slideKey = `intro.slide${index + 1}`;
            return (
              <SwiperSlide key={index}>
                <SlideContent>
                  <SlideTitle>{t(`${slideKey}.title`)}</SlideTitle>
                  <SlideDescription>{t(`${slideKey}.description`)}</SlideDescription>
                  <SlideImageWrapper>
                    <SlideImage
                      src={`${process.env.PUBLIC_URL}/images/intro/${config.asset}`}
                      alt=""
                    />
                  </SlideImageWrapper>
                </SlideContent>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </SwiperWrapper>
      <ButtonWrapper>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleContinue}
          sx={{
            py: 1.5,
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          {t(`intro.slide${activeIndex + 1}.buttonLabel`)}
        </Button>
      </ButtonWrapper>
    </CarouselContainer>
  );
};

export default IntroCarousel;
